import { useEffect, useState } from 'react';
import { owner } from '../services/api';

// Tiny cache so multiple components don't re-fetch /my-tier on every mount.
let _cache = null;
let _pending = null;
const _subscribers = new Set();

export function invalidateTierCache() {
  _cache = null;
  _pending = null;
  _subscribers.forEach((cb) => cb(null));
}

/**
 * useMyTier — returns { tier, loading, error, reload }
 * `tier` shape: { package, label, features[], maxListings, maxCategoriesPerListing,
 *                 currentListings, canAddListing }
 * Shared module-level cache so ManageListings + MyCoupons + MyLoyalty + Sidebar
 * all get the same value without extra network hits.
 */
export function useMyTier() {
  const [tier, setTier] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const onUpdate = (val) => { if (alive) { setTier(val); setLoading(!val); } };
    _subscribers.add(onUpdate);

    if (_cache) { setTier(_cache); setLoading(false); return () => _subscribers.delete(onUpdate); }
    if (!_pending) {
      _pending = owner.myTier()
        .then((data) => { _cache = data; _subscribers.forEach(cb => cb(data)); })
        .catch((err) => { if (alive) setError(err); _pending = null; });
    }
    return () => { alive = false; _subscribers.delete(onUpdate); };
  }, []);

  return {
    tier,
    loading,
    error,
    reload: () => { invalidateTierCache(); /* trigger next mount fetch */ },
    hasFeature: (name) => !!tier?.features?.includes(name),
  };
}
